import { Component, HostBinding, HostListener, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Event } from 'src/app/model/event';
import { AdminService } from 'src/app/service/admin.service';
import { EventsService } from 'src/app/service/events.service';
import { CHAIR_LAYOUTS, ChairLayout, LayoutService } from 'src/app/service/layout.service';

/** One segment in the event hierarchy breadcrumb. Three rendering modes:
 *  - clickable anchor: `link` set, `ellipsis` falsy, not the last segment
 *  - current location: last segment in the array (`current` class)
 *  - elision marker: `ellipsis: true` — non-clickable "…" between segments,
 *    used when more than one intermediate event would otherwise be shown
 *  `tooltip` is an i18n key (translated in the template) and is optional —
 *  segments without a tooltip just don't render one.
 *  `labelStrong` + `labelTrailing` provide a split-render alternative to
 *  `label`: when both are set, the template renders `labelStrong` in a
 *  heavier weight (via `.event-breadcrumb-label-strong`) followed by
 *  `labelTrailing` at the segment default weight. Used for the current
 *  event so the acronym (+ year) reads as the primary handle while the
 *  trailing full name sits quieter. `label` is still set for tooltip /
 *  aria fallbacks. */
export interface EventBreadcrumbSegment {
  label: string;
  link?: string;
  icon?: string;
  ellipsis?: boolean;
  tooltip?: string;
  labelStrong?: string;
  labelTrailing?: string;
}

@Component({
  selector: 'app-event-breadcrumb',
  templateUrl: './event-breadcrumb.component.html',
  styleUrls: ['./event-breadcrumb.component.scss']
})
export class EventBreadcrumbComponent implements OnInit, OnDestroy {
  constructor(
    public adminService: AdminService,
    private eventsService: EventsService,
    private layoutService: LayoutService,
    private ngZone: NgZone,
  ) {}

  // Handler de mousemove registrado FORA da zona (ver ngOnInit). Bound p/ ter a
  // mesma referência em add/removeEventListener.
  private readonly boundDocMouseMove = (ev: MouseEvent) => this.onDocMouseMove(ev);

  // Wide mode — when active, the chair chrome is hidden and the breadcrumb
  // itself slides out of view, only revealing when the cursor approaches
  // the top edge of the viewport. State lives in LayoutService so the chair
  // sidebar can react in lockstep.
  wideModeActive = false;
  navbarRevealed = false;
  currentLayout: ChairLayout = CHAIR_LAYOUTS.MODERN;
  readonly LAYOUT = CHAIR_LAYOUTS;
  private wideSub?: Subscription;
  private layoutSub?: Subscription;

  // Cache de `--chrome-top-offset`. `getComputedStyle` força recálculo de estilo
  // do documento inteiro; chamá-lo a CADA mousemove (como antes) travava páginas
  // com DOM grande (ex.: matriz de sugestões). Recalculado só em eventos raros
  // (ativar modo amplo / troca de layout / resize), nunca no mousemove.
  private chromeTopOffset = 0;

  // Pixel thresholds for the hover-reveal: show when cursor crosses inside
  // REVEAL_AT_PX, hide once cursor moves below HIDE_BELOW_PX (the gap is
  // hysteresis so the bar doesn't flicker if the cursor sits right on the
  // boundary).
  private static readonly REVEAL_AT_PX = 8;
  private static readonly HIDE_BELOW_PX = 60;

  @HostBinding('class.wide-mode')
  get hostWideMode(): boolean { return this.wideModeActive; }

  @HostBinding('class.navbar-revealed')
  get hostNavbarRevealed(): boolean { return this.navbarRevealed; }

  ngOnInit(): void {
    this.wideSub = this.layoutService.wideMode$.subscribe(active => {
      this.wideModeActive = active;
      if (!active) this.navbarRevealed = false;
      else this.refreshChromeTopOffset();
    });
    this.layoutSub = this.layoutService.layout$.subscribe(layout => {
      this.currentLayout = layout;
      this.refreshChromeTopOffset();
    });

    // mousemove FORA da zona: como @HostListener rodava na zona e disparava CD a
    // cada movimento no app inteiro (custoso em páginas de DOM grande). Reentra
    // na zona só quando navbarRevealed muda (raro) — ver onDocMouseMove.
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundDocMouseMove);
    });
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.wideModeActive) { this.refreshChromeTopOffset(); }
  }

  private refreshChromeTopOffset(): void {
    this.chromeTopOffset = this.getChromeTopOffset();
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.boundDocMouseMove);
    this.wideSub?.unsubscribe();
    this.layoutSub?.unsubscribe();
  }

  // Registrado manualmente FORA da zona em ngOnInit (não @HostListener).
  onDocMouseMove(ev: MouseEvent): void {
    if (!this.wideModeActive) return;
    // Desloca os limiares pelo `--chrome-top-offset` global (proxy-bar
    // etc.): em modo amplo a proxy-bar continua visível em y=0–30, então
    // o usuário "se aproximando" do topo significa cruzar pra DENTRO ou
    // pra perto da proxy-bar — não atingir y=0 (que está atrás dela).
    // Sem esse shift, o reveal só dispararia quando o cursor chegasse na
    // borda superior absoluta da viewport, que com proxy-bar fica oculta.
    const chromeOffset = this.chromeTopOffset;   // cacheado — sem getComputedStyle no mousemove
    const revealAt = EventBreadcrumbComponent.REVEAL_AT_PX + chromeOffset;
    const hideBelow = EventBreadcrumbComponent.HIDE_BELOW_PX + chromeOffset;
    const y = ev.clientY;
    const reveal = !this.navbarRevealed && y <= revealAt;
    const hide = this.navbarRevealed && y > hideBelow;
    if (reveal || hide) {
      // Só reentra na zona quando o estado CRUZA o limiar (raro) — navbarRevealed
      // alimenta um HostBinding de classe, então precisa de CD p/ refletir.
      this.ngZone.run(() => { this.navbarRevealed = reveal; });
    }
  }

  /** Lê a custom property global `--chrome-top-offset` (definida em
   *  `_core.scss`, gated por `body.proxy-active`) e devolve em pixels.
   *  Single source of truth — se uma nova camada de chrome for adicionada
   *  no futuro (ex.: install-banner), basta atualizar a regra global e
   *  esse getter passa a refletir o novo total automaticamente. */
  private getChromeTopOffset(): number {
    const raw = getComputedStyle(document.body).getPropertyValue('--chrome-top-offset').trim();
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }

  toggleWideMode(ev?: MouseEvent): void {
    ev?.stopPropagation();
    this.layoutService.toggleWideMode();
  }

  // Hard reload for event-changing navigation. Angular reuses component
  // instances on `:eventId` change, and some child components read
  // `selectedEvent` only in ngOnInit — leaving stale state tied to the
  // previous event. `window.location.assign` forces a full reload so the
  // whole tree re-initialises against the new event.
  navigateHard(event: MouseEvent, url: string): void {
    event.preventDefault();
    window.location.assign(url);
  }

  /** Template-bound: true when the current event has EXACTLY ONE direct
   *  sub-event. In that case the breadcrumb renders the sub-event as a
   *  plain trailing segment (styled like an intermediate), bypassing
   *  the menu — a single child doesn't justify a popup. */
  get hasSingleSubEvent(): boolean {
    return this.subEvents.length === 1;
  }

  /** Template-bound: true when the current event has TWO OR MORE direct
   *  sub-events. The breadcrumb then surfaces the trailing "…" button
   *  that opens the sub-events menu. */
  get hasMultipleSubEvents(): boolean {
    return this.subEvents.length > 1;
  }

  /** Direct sub-events of the current admin event, lazily fetched. The
   *  array is empty until `fetchSubEvents` lands; subsequent CD cycles
   *  return the cached value. Exposed so the (eventual) menu trigger
   *  can iterate over the children when rendering the popup. */
  get subEvents(): Event[] {
    const current = this.adminService?.selectedEvent;
    if (!current?.id) return [];
    return this.fetchedSubEvents.get(current.id) ?? [];
  }

  // (Previous `openSubEventsMenu` stub was removed — the trigger now
  //  uses `[matMenuTriggerFor]="subEventsMenu"` in the template, and
  //  Material handles open/close + outside-click dismissal on its own.)

  // Cache the most recently computed segments keyed by the `selectedEvent`
  // reference. Without this, the template-bound getter would produce a
  // BRAND NEW array (with brand-new inner objects) on every change-
  // detection pass — and CD runs on every mouse event, including hover.
  // The *ngFor's iterable differ would see new identities each cycle,
  // destroy the rendered <a> nodes, and re-create them. That destroys the
  // hover state mid-frame (visible flicker) AND races click handling so
  // mousedown lands on one anchor but mouseup hits the replacement, with
  // no click ever firing. Caching here pins the array reference so the
  // differ sees a stable list and skips re-rendering.
  private cachedEvent: Event | null | undefined = undefined;
  // Sem segmento inicial de home — a função de "voltar pro portal JEMS"
  // vive no ícone home ao lado do logo JEMS no sidebar moderno (cf.
  // `admin-event-sidebar.component.html`).
  private cachedSegments: EventBreadcrumbSegment[] = [];

  // Ancestor lookup state for the case where `event.eventParent` is
  // returned by the API as an ID number (not a hydrated Event object).
  // - `fetchedAncestors` caches every event we've already pulled by ID
  //   so we don't re-fetch on subsequent CD cycles.
  // - `inFlightFetches` dedupes concurrent fetches for the same ID (CD
  //   runs many times per second; we only want one HTTP request per
  //   ancestor).
  // - `failedFetches` records IDs that returned an error so we don't
  //   retry-storm. The user can hit a transient 401/404 (e.g., an
  //   ancestor they don't have permission to read) and we just skip
  //   that part of the chain.
  private fetchedAncestors = new Map<number, Event>();
  private inFlightFetches = new Set<number>();
  private failedFetches = new Set<number>();

  // Sub-events lookup state — analogous to the ancestor cache. The API's
  // `getEvent(id)` doesn't populate `event.subEvents`, so we have to
  // pull direct children via a separate endpoint (`getSubconferencesBy
  // Event`). Cached by current-event id; deduped in-flight; failed ids
  // remembered so we don't retry-storm.
  private fetchedSubEvents = new Map<number, Event[]>();
  private inFlightSubEventsFetches = new Set<number>();
  private failedSubEventsFetches = new Set<number>();

  // Safety cap on the recursive walk: protects against pathological
  // event hierarchies (cycles, accidentally-deep nesting). 10 is well
  // above any realistic conference hierarchy.
  private static readonly MAX_ANCESTOR_DEPTH = 10;

  /** Template-bound getter — returns the cached segments unless the
   *  underlying event reference changes (which only happens on route
   *  navigation, when `AdminService.fetchSelectedEvent` assigns a fresh
   *  Event instance). Steady state hits the cache fast path. */
  get segments(): EventBreadcrumbSegment[] {
    const current = this.adminService?.selectedEvent ?? null;
    if (current === this.cachedEvent) {
      return this.cachedSegments;
    }
    this.cachedEvent = current;
    this.cachedSegments = current
      ? this.buildSegments(this.collectIntermediates(current), current)
      : [];
    // Kick off the sub-events fetch for this event id (if we haven't
    // already). The result populates `fetchedSubEvents` and invalidates
    // the segment cache so the next CD pass re-evaluates `hasSubEvents`,
    // surfacing the trailing "…" trigger if children exist.
    if (current?.id != null) {
      this.fetchSubEvents(current.id);
    }
    return this.cachedSegments;
  }

  /** Walks the `eventParent` chain upward, collecting hydrated ancestors
   *  and lazily fetching any ID-only parents from `EventsService`. Result
   *  is ordered root → … → direct parent (closest to current at the end).
   *
   *  The walk is synchronous from the caller's perspective: any unresolved
   *  parent ID kicks off an async fetch and short-circuits the chain at
   *  that point. When the fetch lands, the segment cache is invalidated
   *  and the next CD cycle resumes the walk one level higher, eventually
   *  completing the chain across multiple CD ticks. The user sees the
   *  breadcrumb grow as ancestors arrive — a touch progressive, but
   *  reliable and easy to reason about. */
  private collectIntermediates(current: Event): Event[] {
    const chain: Event[] = [];
    let cursor: Event | number | undefined = current?.eventParent;
    let depth = 0;
    while (cursor && depth < EventBreadcrumbComponent.MAX_ANCESTOR_DEPTH) {
      if (typeof cursor === 'object') {
        // Already hydrated — use directly and step up.
        chain.unshift(cursor);
        cursor = cursor.eventParent;
      } else if (typeof cursor === 'number') {
        const id: number = cursor;
        if (this.failedFetches.has(id)) {
          // Don't keep retrying ancestors that errored — just stop the
          // walk here; the rest of the chain stays unknown.
          break;
        }
        const cached = this.fetchedAncestors.get(id);
        if (cached) {
          chain.unshift(cached);
          cursor = cached.eventParent;
        } else {
          // Not yet in cache — schedule a fetch and stop the walk.
          // Subsequent CD cycles, once the fetch lands, will continue
          // from this point.
          this.fetchAncestor(id);
          break;
        }
      } else {
        break;
      }
      depth++;
    }
    return chain;
  }

  /** Lazily fetches the direct sub-events of an event by ID. Same dedupe
   *  pattern as `fetchAncestor`: in-flight Set short-circuits concurrent
   *  calls, failed Set prevents retry-storms on errors. On success,
   *  invalidates the segment cache so the template re-renders with the
   *  newly available `hasSubEvents` value on the next CD tick. */
  private fetchSubEvents(id: number): void {
    if (this.inFlightSubEventsFetches.has(id)) return;
    if (this.failedSubEventsFetches.has(id)) return;
    if (this.fetchedSubEvents.has(id)) return;
    this.inFlightSubEventsFetches.add(id);
    this.eventsService.getSubconferencesByEvent(id).subscribe({
      next: (subs: Event[]) => {
        this.fetchedSubEvents.set(id, subs ?? []);
        this.inFlightSubEventsFetches.delete(id);
        // Reset the segment cache so the next CD re-evaluates
        // `hasSubEvents` (and the trailing "…" trigger surfaces).
        this.cachedEvent = undefined;
      },
      error: () => {
        this.failedSubEventsFetches.add(id);
        this.inFlightSubEventsFetches.delete(id);
      },
    });
  }

  /** Lazily fetches a single ancestor event by ID. Deduplicates concurrent
   *  requests (CD runs many times per second; we want at most one HTTP
   *  per ID), and records failures so we don't retry-storm on errors.
   *  On success, invalidates the segment cache so the template re-renders
   *  with the newly available ancestor on the next CD tick. */
  private fetchAncestor(id: number): void {
    if (this.inFlightFetches.has(id)) return;
    if (this.failedFetches.has(id)) return;
    this.inFlightFetches.add(id);
    this.eventsService.getEvent(id).subscribe({
      next: (event: Event) => {
        this.fetchedAncestors.set(id, event);
        this.inFlightFetches.delete(id);
        // Reset the cache key so the getter recomputes — the next CD
        // pass will pick up the new ancestor and (if its `eventParent`
        // is itself another ID) kick off the next fetch up the chain.
        this.cachedEvent = undefined;
      },
      error: () => {
        this.failedFetches.add(id);
        this.inFlightFetches.delete(id);
      },
    });
  }

  /** Applies the rules (sem home — cf. cachedSegments):
   *  - 0 intermediates: só o evento current
   *  - 1 intermediate: acronym do intermediário + current
   *  - 2+ intermediates: "…" elision marker, LAST intermediate acronym, current */
  private buildSegments(intermediates: Event[], current: Event): EventBreadcrumbSegment[] {
    const out: EventBreadcrumbSegment[] = [];

    if (intermediates.length > 1) {
      out.push({ label: '…', ellipsis: true });
      const last = intermediates[intermediates.length - 1];
      out.push({
        label: last.acronym,
        link: `/admin/${last.id}`,
        tooltip: 'menus.sidebar.parent-event',
      });
    } else if (intermediates.length === 1) {
      const only = intermediates[0];
      out.push({
        label: only.acronym,
        link: `/admin/${only.id}`,
        tooltip: 'menus.sidebar.parent-event',
      });
    }

    // Current segment links to the event's "Informações" page — the admin
    // settings view at `/admin/:eventId/settings`. Split label: the
    // acronym (+ year if present) renders bolder; the " - name" suffix
    // sits at the default weight, matching the rest of the bar.
    const acronymYear = current.year
      ? `${current.acronym} ${current.year}`
      : current.acronym;
    const trailing = current.name ? ` - ${current.name}` : '';
    out.push({
      label: this.formatCurrentLabel(current),
      labelStrong: acronymYear,
      labelTrailing: trailing,
      link: `/admin/${current.id}/settings`,
    });
    return out;
  }

  /** "{acronym} {year} - {name}" with graceful fallbacks if year or name
   *  are missing — keeps the breadcrumb robust against partial event data
   *  while still favouring the full canonical form when everything is set. */
  private formatCurrentLabel(event: Event): string {
    const acronymYear = event.year ? `${event.acronym} ${event.year}` : event.acronym;
    return event.name ? `${acronymYear} - ${event.name}` : acronymYear;
  }
}
